// Типы для финансовой статистики
export interface FinancialStats {
    user_money: {
        usd_total: number;
        usdt_total: number;
    };
    guarantees: {
        strategy_6_weekly: number;
        strategy_6_total: number;
    };
    investments: Record<
        string,
        {
            active: number;
            pending: number;
            withdraw_pending: number;
            transfer_pending: number;
        }
    >;
}

// Типы для статистики пополнений
export interface PaymentIncomeStats {
    last24h: {
        total: number;
        count: number;
        total_hub: number;
        total_game: number;
    };
    last7d: {
        total: number;
        count: number;
        total_hub: number;
        total_game: number;
    };
    last30d: {
        total: number;
        count: number;
        total_hub: number;
        total_game: number;
    };
}

// Типы для статистики выводов
export interface WithdrawalStats {
    last24h: {
        total: number;
        count: number;
        total_hub: number;
        total_game: number;
    };
    last7d: {
        total: number;
        count: number;
        total_hub: number;
        total_game: number;
    };
    last30d: {
        total: number;
        count: number;
        total_hub: number;
        total_game: number;
    };
}

// Типы для статистики всех пользователей
export interface AllUsersStats {
    total: number;
    by_status: {
        active: {
            total: number;
            paid: number;
            unpaid: number;
        };
        blocked: number;
        restricted: number;
        deleted: number;
    };
}

// Типы для статистики пользователей из игры / VIP
export interface GameStatsItem {
    label: string;
    registrations: number;
    vip_active: number;
    vip_dropped: number;
    bought_vip_ever?: number;
    first_vip_same_month?: number;
    conversion_pct?: number;
}

export interface GameVipStatsData {
    generated_at: string;
    scope: string;
    notes: Record<string, string>;
    /** Все VIP в структуре сейчас (дерево + access_status=0) */
    structure_overview?: {
        in_structure_total: number;
        paid_vip_active: number;
        complimentary_vip_active: number;
        effective_vip_active: number;
        paid_vip_from_game: number;
        paid_vip_not_from_game: number;
        definition: string;
    };
    /** По месяцам: покрытие VIP + платежи по всей структуре */
    monthly_structure?: Array<{
        label: string;
        paid_covering_month_end: number;
        payments: number;
        unique_payers: number;
    }>;
    overview: {
        game_users_total: number;
        paid_vip_active: number;
        paid_vip_ever_marker: number;
        paid_vip_expired_or_inactive: number;
        active_renewers: number;
        active_first_period_only: number;
        complimentary_vip_active: number;
    };
    current_month: {
        label: string;
        first_purchases: number;
        renewals: number;
        total_payments: number;
    };
    deferred_first_purchase: {
        month_label: string;
        registered_over_3_months_first_vip_this_month: number;
        registered_over_6_months_first_vip_this_month: number;
    };
    new_users_same_month: {
        label: string;
        registrations: number;
        first_vip_same_month: number;
        conversion_pct: number;
    };
    monthly_cohorts: GameStatsItem[];
    retention: {
        eligible_m2: number;
        retained_m2: number;
        retention_m2_pct: number;
        eligible_m3: number;
        retained_m3: number;
        retention_m3_pct: number;
        definition: string;
    };
    time_to_first_vip: {
        sample_size: number;
        avg_days: number | null;
        median_days: number | null;
    };
    renewal_distribution: {
        payments_1: number;
        payments_2: number;
        payments_3_plus: number;
    };
    shop_vip?: {
        total_purchases: number;
        unique_users: number;
        amount_sum: number;
        current_month: {
            label: string;
            purchases: number;
            unique_users: number;
            amount_sum: number;
        };
        by_month: Array<{
            label: string;
            purchases: number;
            unique_users: number;
            amount_sum: number;
        }>;
    };
}

// Типы для списка платежей
export interface PaymentIncomeListItem {
    id: number;
    user_id: number;
    user_name: string;
    user_surname: string;
    user_email: string;
    amount: number;
    currency: string;
    payment_method: string;
    source_table: string;
    source_id: number;
    status: string;
    transaction_hash: string | null;
    payment_time: string | null;
    created_at: string | null;
    source: string | null;
}

export interface PaymentIncomeListParams {
    per_page?: number;
    page?: number;
    sort_by?: 'payment_time' | 'amount' | 'created_at' | 'user_id';
    sort_order?: 'asc' | 'desc';
    user_id?: number;
    payment_method?: string;
    status?: string;
    source_table?: string;
    date_from?: string;
    date_to?: string;
    source?: string;
}

export interface PaymentIncomeListResponse {
    success: boolean;
    data: PaymentIncomeListItem[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number | null;
        to: number | null;
    };
}

export interface PaymentIncomeDetailsResponse {
    success: boolean;
    data: {
        payment_income: PaymentIncomeListItem;
        source_data: Record<string, any>;
    };
}

export const financialEndpoints = {
    getFinancialStats: {
        query: () => "/admin/dashboard/financial-stats",
    },
    getPaymentIncomeStats: {
        query: () => "/admin/dashboard/payment-income-stats",
    },
    getWithdrawalStats: {
        query: () => "/admin/dashboard/withdrawal-stats",
    },
    getAllUsersStats: {
        query: () => "/admin/dashboard/all-users-stats",
    },
    getGameStats: {
        query: () => "/users/game-stats",
    },
    getGameVipStats: {
        query: () => "/admin/dashboard/game-vip-stats",
    },
    getPaymentIncomeList: {
        query: (params?: PaymentIncomeListParams) => {
            const queryParams = new URLSearchParams();
            if (params?.per_page) {
                queryParams.append('per_page', params.per_page.toString());
            }
            if (params?.page) {
                queryParams.append('page', params.page.toString());
            }
            if (params?.sort_by) {
                queryParams.append('sort_by', params.sort_by);
            }
            if (params?.sort_order) {
                queryParams.append('sort_order', params.sort_order);
            }
            if (params?.user_id) {
                queryParams.append('user_id', params.user_id.toString());
            }
            if (params?.payment_method) {
                queryParams.append('payment_method', params.payment_method);
            }
            if (params?.status) {
                queryParams.append('status', params.status);
            }
            if (params?.source_table) {
                queryParams.append('source_table', params.source_table);
            }
            if (params?.date_from) {
                queryParams.append('date_from', params.date_from);
            }
            if (params?.date_to) {
                queryParams.append('date_to', params.date_to);
            }
            if (params?.source) {
                queryParams.append('source', params.source);
            }
            const queryString = queryParams.toString();
            return `/admin/payment-income/list${queryString ? `?${queryString}` : ''}`;
        },
    },
    getPaymentIncomeDetails: {
        query: (id: number) => `/admin/payment-income/${id}/details`,
    },
};

export const financialTags = ["FinancialStats", "PaymentIncomeStats", "WithdrawalStats", "AllUsersStats"] as const;
